import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge, Stack, Tabs } from '@mantine/core';
import AllocatedWeight from '@/components/app/deals/steps/forwarding/AllocatedWeight';
import DeliveredWeight from '@/components/app/deals/steps/forwarding/DeliveredWeight';
import ForwardingActionZone from '@/components/app/deals/steps/forwarding/ForwardingActionZone';
import ForwardingDocsTab from '@/components/app/deals/steps/forwarding/ForwardingDocsTab';
import ForwardingFunnel from '@/components/app/deals/steps/forwarding/ForwardingFunnel';
import ForwardingGoodsHeader from '@/components/app/deals/steps/forwarding/ForwardingGoodsHeader';
import InTransit from '@/components/app/deals/steps/forwarding/InTransit';
import LoadedWeight from '@/components/app/deals/steps/forwarding/LoadedWeight';
import { dailyCapacityTon, defaultForwardingTab, type ForwardingStageKey, pendingUpdCount } from '@/lib/forwardingView';
import type { BargainStepsInfo } from '@/types/deals';

interface ForwardingWeightsProps {
  readonly info: BargainStepsInfo;
  /** Живой шаг → доступны зона действий и подпись УПД. В read-only сводке не передаётся. */
  readonly dealId?: number;
}

const DOCS_TAB = 'docs';

// Шаг «Перевозка»: шапка → закреплённая зона действий (live) → дашборд-воронка (клик → таб) → 5 табов
// (Разобрано/Отгружено/В пути/Доставлено/Документы). Деталь этапа — в табе (compact, без большой сводки).
// Используется и в живом шаге (ForwardingStep), и в read-only сводке (DealStepReadonly).
export default function ForwardingWeights({ info, dealId }: ForwardingWeightsProps) {
  const { t } = useTranslation();
  const fwd = info.forwardingInfo;
  const [tab, setTab] = useState<string>(defaultForwardingTab(fwd));
  const pendingUpd = pendingUpdCount(info);
  const activeStage = tab === DOCS_TAB ? undefined : (tab as ForwardingStageKey);

  return (
    <Stack gap="lg">
      <ForwardingGoodsHeader info={info} />

      {dealId != null ? (
        <ForwardingActionZone info={info} dealId={dealId} onGoToSign={() => setTab(DOCS_TAB)} />
      ) : null}

      {fwd ? <ForwardingFunnel info={fwd} active={activeStage} onSelect={setTab} /> : null}

      <Tabs value={tab} onChange={(value) => setTab(value ?? defaultForwardingTab(fwd))} variant="pills" radius="md" keepMounted={false}>
        <Tabs.List grow>
          <Tabs.Tab value="allocated">{t('app.deals.forwarding.allocatedDone')}</Tabs.Tab>
          <Tabs.Tab value="loaded">{t('app.deals.forwarding.loaded')}</Tabs.Tab>
          <Tabs.Tab value="ongoing">{t('app.deals.forwarding.ongoing')}</Tabs.Tab>
          <Tabs.Tab value="unloaded">{t('app.deals.forwarding.unloaded')}</Tabs.Tab>
          <Tabs.Tab
            value={DOCS_TAB}
            rightSection={
              pendingUpd > 0 ? (
                <Badge size="sm" circle color="brand" variant="filled">
                  {pendingUpd}
                </Badge>
              ) : undefined
            }
          >
            {t('app.deals.forwarding.tabDocs')}
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="allocated" pt="md">
          {fwd ? (
            <AllocatedWeight
              info={fwd}
              dailyCapacityTon={dailyCapacityTon(info.loadingConditions?.dailyShippingRate)}
              dealVolumeTon={info.plannedWeight}
            />
          ) : null}
        </Tabs.Panel>
        <Tabs.Panel value="loaded" pt="md">
          {fwd ? <LoadedWeight info={fwd} /> : null}
        </Tabs.Panel>
        <Tabs.Panel value="ongoing" pt="md">
          {fwd ? <InTransit info={fwd} /> : null}
        </Tabs.Panel>
        <Tabs.Panel value="unloaded" pt="md">
          {fwd ? <DeliveredWeight info={fwd} /> : null}
        </Tabs.Panel>
        <Tabs.Panel value={DOCS_TAB} pt="md">
          <ForwardingDocsTab info={info} dealId={dealId} />
        </Tabs.Panel>
      </Tabs>
    </Stack>
  );
}
